import { Button } from "@/components/ui/button";
import { Search, Wallet, Menu, ChevronDown, LogOut, Copy, ExternalLink, Rocket } from "lucide-react";
import { useState } from "react";
import { useWallet } from "@/contexts/WalletContext";
import { useIsAdmin } from "@/hooks/use-admin";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { Logo } from "@/components/layout/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { NavLink } from "@/components/NavLink";
import { 
  Home, 
  Flame, 
  Trophy, 
  BarChart3, 
  Users, 
  Settings, 
  Radio,
  Sparkles,
  Globe,
  Store,
  MessageSquare,
  Crosshair,
  DollarSign,
  UserCheck,
  Fish,
  Code,
  HelpCircle,
  BookOpen,
  FileText,
  Shield
} from "lucide-react";

const mobileNavItems = [
  { icon: Home, label: "Home", href: "/" },
  { icon: BarChart3, label: "Dashboard", href: "/dashboard" },
  { icon: Globe, label: "Discover", href: "/discover" },
  { icon: Flame, label: "Trending", href: "/trending" },
  { icon: Sparkles, label: "New", href: "/new" },
  { icon: Trophy, label: "Leaderboard", href: "/leaderboard" },
  { icon: BarChart3, label: "Portfolio", href: "/portfolio" },
  { icon: Radio, label: "Streams", href: "/streams" },
  { icon: MessageSquare, label: "Community Hub", href: "/community-hub" },
  { icon: Store, label: "Marketplace", href: "/marketplace" },
];

const mobileToolsItems = [
  { icon: Crosshair, label: "Token Sniper", href: "/sniper" },
  { icon: DollarSign, label: "Copy Subs", href: "/copy-subscriptions" },
  { icon: UserCheck, label: "Copy Trading", href: "/copy-trading" },
  { icon: Fish, label: "Whale Tracker", href: "/whales" },
  { icon: Radio, label: "Streamer Dashboard", href: "/streamer" },
  { icon: Code, label: "API Access", href: "/api" },
];

const mobileResourceItems = [
  { icon: HelpCircle, label: "Support", href: "/support" },
  { icon: BookOpen, label: "Docs", href: "/docs" },
  { icon: FileText, label: "Terms", href: "/terms" },
  { icon: Settings, label: "Settings", href: "/settings" },
];

export function Header() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { connected, connecting, address, balance, connect, disconnect, publicKey } = useWallet();
  const { isAdmin } = useIsAdmin();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/trending?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
    }
  };

  const copyAddress = () => {
    if (publicKey) {
      navigator.clipboard.writeText(publicKey);
      toast({ title: "Address Copied", description: "Wallet address copied to clipboard" });
    }
  };

  const viewOnExplorer = () => {
    if (publicKey) {
      window.open(`https://solscan.io/account/${publicKey}?cluster=devnet`, "_blank");
    }
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="container flex h-16 items-center justify-between gap-4">
        {/* Logo */}
        <Logo size="md" linkTo="/" />

        {/* Search Bar - Desktop */}
        <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
          <div className="relative w-full group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
            <input
              type="text"
              placeholder="Search coins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-lg bg-secondary border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {/* Mobile Search Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
          >
            <Search className="h-5 w-5" />
          </Button>

          <Button variant="hero" className="hidden sm:flex" asChild>
            <Link to="/create">
              <Rocket className="h-4 w-4" />
              Create Coin
            </Link>
          </Button>

          {connected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="group">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                    <span className="font-mono text-sm">{address}</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-xs text-muted-foreground">Balance</p>
                  <p className="font-mono font-bold text-foreground">{balance.toFixed(4)} SOL</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={copyAddress}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Address
                </DropdownMenuItem>
                <DropdownMenuItem onClick={viewOnExplorer}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View on Explorer
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={disconnect} className="text-destructive focus:text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Disconnect
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              variant="outline" 
              className="group" 
              onClick={connect}
              disabled={connecting}
            >
              <Wallet className={cn(
                "h-4 w-4 transition-transform group-hover:scale-110",
                connecting && "animate-pulse"
              )} />
              <span className="hidden sm:inline">
                {connecting ? "Connecting..." : "Connect Wallet"}
              </span>
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] p-0">
              <SheetHeader className="p-4 border-b border-border">
                <SheetTitle className="flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-primary" />
                  <span className="font-display">TheMoonRun</span>
                </SheetTitle>
              </SheetHeader>
              <nav className="p-4 space-y-1 overflow-y-auto max-h-[60vh]">
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">Main</p>
                {mobileNavItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground transition-all duration-200 hover:bg-secondary"
                    activeClassName="bg-primary/10 text-primary"
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}
                
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase mt-4">Trading Tools</p>
                {mobileToolsItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground transition-all duration-200 hover:bg-secondary"
                    activeClassName="bg-primary/10 text-primary"
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}
                
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase mt-4">Resources</p>
                {mobileResourceItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground transition-all duration-200 hover:bg-secondary"
                    activeClassName="bg-primary/10 text-primary"
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}
                
                {/* Admin link - only for admins */}
                {isAdmin && (
                  <NavLink
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-destructive transition-all duration-200 hover:bg-destructive/10"
                    activeClassName="bg-destructive/10"
                  >
                    <Shield className="h-5 w-5" />
                    <span className="font-medium">Admin</span>
                  </NavLink>
                )}
              </nav>
              <div className="p-4 border-t border-border">
                <Link 
                  to="/create" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Rocket className="h-4 w-4" />
                  Create Coin
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Mobile Search Bar */}
      {isSearchOpen && (
        <form onSubmit={handleSearch} className="md:hidden px-4 pb-4 animate-fade-in">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search coins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-lg bg-secondary border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              autoFocus
            />
          </div>
        </form>
      )}
    </header>
  );
}